import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Book as BookIcon,
  Quiz as QuizIcon,
  EmojiEvents as ExamIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  HelpOutline as HelpIcon,
  ContactMail as ContactIcon,
  Psychology as StrategyIcon,
  AdminPanelSettings as AdminIcon,
  Gavel as LegalIcon,
  ExitToApp as ExitToAppIcon,
  Person as PersonIcon,
  PregnantWoman as MidwiferyIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useActivityTracking } from '../hooks/useActivityTracking';
import TermsModal from './TermsModal';

import { SHOW_MIDWIVES_REVIEWER } from '../config/featureFlags';

const drawerWidth = 280;

interface MainLayoutProps {
  children: React.ReactNode;
}

const nursingMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Study Handbook', icon: <BookIcon />, path: '/handbook' },
  { text: 'Practice Tests', icon: <QuizIcon />, path: '/practice-tests' },
  { text: 'Mock Exam', icon: <ExamIcon />, path: '/mock-exam' },
  { text: 'Test Strategies', icon: <StrategyIcon />, path: '/strategies' },
  { text: 'Progress Tracking', icon: <TrendingUpIcon />, path: '/progress' },
  { text: 'FAQ', icon: <HelpIcon />, path: '/faq' },
  { text: 'Contact Us', icon: <ContactIcon />, path: '/contact' },
  { text: 'Legal Policy', icon: <LegalIcon />, path: '/legal' },
];

const midwiferyMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  ...(SHOW_MIDWIVES_REVIEWER ? [{ text: 'Midwife Reviewer', icon: <MidwiferyIcon />, path: '/midwives' }] : []),
  ...(SHOW_MIDWIVES_REVIEWER ? [{ text: 'Study Guide', icon: <BookIcon />, path: '/midwives/study' }] : []),
  ...(SHOW_MIDWIVES_REVIEWER ? [{ text: 'Mock Exam', icon: <ExamIcon />, path: '/midwives/mock' }] : []),
  { text: 'Progress Tracking', icon: <TrendingUpIcon />, path: '/progress' },
  { text: 'Subscribe', icon: <SchoolIcon />, path: '/subscribe' },
  { text: 'FAQ', icon: <HelpIcon />, path: '/faq' },
  { text: 'Contact Us', icon: <ContactIcon />, path: '/contact' },
  { text: 'Legal Policy', icon: <LegalIcon />, path: '/legal' },
];

export default function MainLayout({ children }: MainLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, isAuthenticated, isAdmin, track, impersonating, login, logout, refetch } = useAuth();
  const isMidwifery = track === 'midwifery';
  const menuItems = isMidwifery ? midwiferyMenuItems : nursingMenuItems;

  const handleStopImpersonating = async () => {
    try {
      await fetch('/api/admin/impersonate', { method: 'DELETE', credentials: 'include' });
      await refetch();
      navigate('/admin');
    } catch (e) {
      console.error('Failed to stop impersonating', e);
    }
  };

  useActivityTracking(user);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleAvatarClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMidwifery ? (
            <MidwiferyIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          ) : (
            <SchoolIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          )}
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
            {isMidwifery ? 'Midwife Mastery' : 'Prometric RN Guide'}
          </Typography>
        </Box>
      </Toolbar>
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {isMidwifery ? 'Prometric • DHA • DOH • NHRA' : 'HAAD • DHA • Prometric'}
        </Typography>
        <Typography variant="caption" display="block">
          {isMidwifery ? 'Gulf Midwifery Licensure Review' : 'Saudi • UAE • Qatar • Kuwait • Oman'}
        </Typography>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => { if (isMobile) setMobileOpen(false); }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                  '& .MuiListItemIcon-root': { color: 'white' },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        {isAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to="/admin"
              selected={location.pathname === '/admin'}
              onClick={() => { if (isMobile) setMobileOpen(false); }}
              sx={{
                bgcolor: 'warning.light',
                color: 'white',
                '& .MuiListItemIcon-root': { color: 'white' },
                '&:hover': { bgcolor: 'warning.main' },
                '&.Mui-selected': { backgroundColor: 'warning.light', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } },
              }}
            >
              <ListItemIcon><AdminIcon /></ListItemIcon>
              <ListItemText primary="Admin" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {isMidwifery ? 'Simplify Midwife Mastery' : 'Prometric RN Exam Guide'}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isAuthenticated && user ? (
              <>
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" noWrap>
                    {user.firstName || user.email}
                  </Typography>
                </Box>
                <IconButton onClick={handleAvatarClick} sx={{ p: 0 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                    Sign Out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  component={RouterLink}
                  to="/subscribe"
                  color="inherit"
                  variant="contained"
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    mr: 1,
                  }}
                >
                  Subscribe
                </Button>
                <Button
                  color="inherit"
                  variant="outlined"
                  startIcon={<LoginIcon />}
                  onClick={login}
                  sx={{
                    borderColor: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Login
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // RN pricing only: let this flex item shrink on narrow screens.
          ...(!isMidwifery && location.pathname === '/subscribe' ? { minWidth: 0 } : {}),
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        {impersonating && (
          <Alert
            severity="warning"
            icon={false}
            sx={{
              mb: 2,
              bgcolor: '#ff6f00',
              color: 'white',
              fontWeight: 'bold',
              alignItems: 'center',
              '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                startIcon={<ExitToAppIcon />}
                onClick={handleStopImpersonating}
                sx={{ borderColor: 'white', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
              >
                Exit
              </Button>
            }
          >
            Viewing as: <strong>{impersonating.name || impersonating.email}</strong> ({impersonating.email})
          </Alert>
        )}
        <TermsModal />
        {children}
        <Box
          component="footer"
          sx={{
            mt: 6,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          {/* Crawlable link cluster — always rendered for SEO */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: { xs: 1.5, sm: 3 },
              mb: 2,
            }}
          >
            {isMidwifery ? (
              <>
                <RouterLink to="/midwifery/subscribe" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Subscribe</RouterLink>
                <RouterLink to="/midwives/trial" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Free Trial</RouterLink>
                <RouterLink to="/midwives/free" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Practice Questions</RouterLink>
                <RouterLink to="/midwives/sample-module" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Study Module Preview</RouterLink>
                <RouterLink to="/faq" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>FAQ</RouterLink>
                <RouterLink to="/contact" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Contact</RouterLink>
                <RouterLink to="/legal" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Legal</RouterLink>
              </>
            ) : (
              <>
                <RouterLink to="/subscribe" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Subscribe</RouterLink>
                <RouterLink to="/free-gulf-rn-reviewer" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Free 100-Question Reviewer</RouterLink>
                <RouterLink to="/free-sample" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Free Sample Test</RouterLink>
                <RouterLink to="/faq" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>FAQ</RouterLink>
                <RouterLink to="/contact" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Contact Us</RouterLink>
                <RouterLink to="/legal" style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.8rem' }}>Legal Policy</RouterLink>
              </>
            )}
          </Box>
          {/* Facebook Page link — shown site-wide on every page */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <a
              href="https://www.facebook.com/profile.php?id=61581904329875"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our Facebook Page"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: 'inherit',
                textDecoration: 'none',
                fontSize: '0.8rem',
              }}
            >
              <FacebookIcon sx={{ fontSize: 18, color: '#1877F2' }} />
              Follow us on Facebook
            </a>
          </Box>
          {isMidwifery && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 900, mx: 'auto', lineHeight: 1.7 }}>
              <strong>Disclaimer:</strong> Simplify Midwife Prometric Mastery™ is an independent exam
              preparation platform. It is not affiliated with, endorsed by, or connected to Prometric,
              Pearson VUE, DHA, DHP/MOPH, NHRA, DOH, HAAD, SCFHS, WHO, ICM, FIGO, or any licensing
              authority. All questions are original educational content created solely for examination
              preparation and do not reflect actual exam content.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
